import { Capacitor } from '@capacitor/core';
import { supabase } from './supabase';

/* =============================================================================
 * Getting a phone onto the list
 * -----------------------------------------------------------------------------
 * A notification can only reach a handset the server knows a token for. This is
 * the whole of that: ask permission, take the token Firebase issues, and record
 * it against the signed-in user.
 *
 * -----------------------------------------------------------------------------
 * Why every failure here is stored rather than thrown
 * -----------------------------------------------------------------------------
 * Push is the one feature where a silent failure is indistinguishable from
 * nothing happening. A man who was never told about an unclosed shift and a man
 * whose phone was never registered look identical from the outside — both saw
 * no notification.
 *
 * So the outcome of registration is written down, and the notification settings
 * screen shows how many phones are actually reachable. "Alerts are on" next to
 * "0 phones reachable" is a sentence somebody can act on. A switch that says on
 * while nothing can arrive is not.
 * ========================================================================== */

export type PushState =
  | { status: 'unsupported'; reason: string }
  | { status: 'denied'; reason: string }
  | { status: 'registered'; token: string }
  | { status: 'error'; reason: string };

let lastState: PushState | null = null;

/** What happened the last time registration ran, for the screens to report. */
export function pushState(): PushState | null {
  return lastState;
}

/**
 * Registers this device for push, if it can be.
 *
 * Safe to call on every sign-in. Firebase reissues the same token for the same
 * install, and the upsert below keys on the token, so repeat calls settle to
 * one row rather than accumulating — which matters because a duplicate row is a
 * man receiving every notification twice, and the fix people reach for is
 * turning notifications off.
 */
export async function registerForPush(userId: string, companyId: string): Promise<PushState> {
  /* The browser is not a failure worth reporting as one. The office dashboard
     has the bell, which is the record; push is what the phone adds. */
  if (!Capacitor.isNativePlatform()) {
    lastState = {
      status: 'unsupported',
      reason: 'Push needs the installed app. The dashboard shows notifications in the bell.',
    };
    return lastState;
  }

  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');

    /* Android 13 and later will not show a single notification until the user
       has agreed, and an app that never asks simply appears broken — nothing
       arrives and nothing explains why. `checkPermissions` first so a man who
       has already agreed is not asked again on every sign-in. */
    let permission = await PushNotifications.checkPermissions();
    if (permission.receive === 'prompt' || permission.receive === 'prompt-with-rationale') {
      permission = await PushNotifications.requestPermissions();
    }

    if (permission.receive !== 'granted') {
      lastState = {
        status: 'denied',
        reason: 'Notifications are switched off for this app. Turn them on in the '
              + "phone's own Settings, under Apps, then sign in again.",
      };
      return lastState;
    }

    /* Kept so they can be taken off again. Without that, every sign-in adds
       another pair and a man who signs in three times has his token written
       three times for one Firebase reply. */
    const listeners: Array<{ remove: () => Promise<void> }> = [];

    const token = await new Promise<string>((resolve, reject) => {
      /* Both outcomes have to be waited for. `register()` resolves as soon as
         the request is made, not when Firebase answers — awaiting it alone
         returns before there is any token to store, and the device silently
         never appears on the list. */
      const timer = setTimeout(
        () => reject(new Error('Firebase did not issue a token within 20 seconds. '
                             + 'Check the phone has Google Play services and a connection.')),
        20_000,
      );

      void PushNotifications.addListener('registration', (t) => {
        clearTimeout(timer);
        resolve(t.value);
      }).then((handle) => listeners.push(handle));

      void PushNotifications.addListener('registrationError', (e) => {
        clearTimeout(timer);
        reject(new Error(String(e.error ?? 'Firebase refused to register this device.')));
      }).then((handle) => listeners.push(handle));

      void PushNotifications.register();
    }).finally(() => {
      for (const handle of listeners) void handle.remove();
    });

    const { Device } = await import('@capacitor/device');
    const info = await Device.getInfo();

    const { error } = await supabase
      .from('push_devices')
      .upsert({
        company_id: companyId,
        user_id: userId,
        platform: Capacitor.getPlatform() === 'ios' ? 'ios' : 'android',
        token,
        model: [info.manufacturer, info.model].filter(Boolean).join(' ') || null,
        app_version: __APP_VERSION__,
        enabled: true,
        last_seen_at: new Date().toISOString(),
        // A handset that was retired for a dead token and has come back with a
        // working one is a live phone again, not a permanent failure.
        failed_at: null,
        fail_reason: null,
      }, { onConflict: 'token' });

    if (error) throw error;

    lastState = { status: 'registered', token };
    return lastState;
  } catch (caught) {
    lastState = { status: 'error', reason: (caught as Error).message };
    return lastState;
  }
}

/**
 * Takes this device off the list.
 *
 * Called on sign-out, and it matters more than it looks: a shared site handset
 * signed out by one supervisor and into by the next would otherwise keep
 * delivering the first man's notifications to the second.
 */
export async function unregisterPush(): Promise<void> {
  const state = lastState;
  lastState = null;
  if (state?.status !== 'registered') return;

  /* The tap listener deliberately survives sign-out. It is attached once for
     the life of the process, and `removeAllListeners()` here — which is what
     this used to do — would take it away for good: the next man to sign in on
     this handset would have notifications that open the app and then sit on the
     dashboard, with nothing to explain why. */
  tapHandler = null;

  try {
    await supabase.from('push_devices').delete().eq('token', state.token);
  } catch {
    /* Best effort. Failing to sign out because a token could not be withdrawn
       would trap somebody on a phone that is not theirs, which is worse than
       the stale row this leaves behind — and the row is visible in the
       notification settings, where it can be removed. */
  }
}

/* =============================================================================
 * What to do when a notification is tapped
 * -----------------------------------------------------------------------------
 * The `url` travelling in the data payload is the screen the notification is
 * about. Opening the app to the dashboard instead makes the man hunt for the
 * thing he was just told about, which is most of the reason people stop
 * reading notifications.
 *
 * -----------------------------------------------------------------------------
 * THE TAP THAT STARTED THE APP
 * -----------------------------------------------------------------------------
 * This listener used to be attached from the shell's effect, which runs after
 * React has mounted and the session has loaded. That works for a tap while the
 * app is already open, and it is the wrong way round for the common case.
 *
 * Tapping a notification on a phone where the app is not running *launches* the
 * app. Capacitor fires pushNotificationActionPerformed as the plugin comes up —
 * long before React has rendered anything — and the plugin does not queue the
 * event for a listener attached later. So the event went nowhere and the app
 * opened wherever it opens, every time, which is exactly the report: tapping
 * any notification lands on the same screen.
 *
 * Two changes. The listener attaches at start-up rather than from a component,
 * and a tap that arrives before there is a router to receive it is *kept* and
 * handed over the moment one appears. Nothing is dropped for being early.
 * ========================================================================== */

let tapHandler: ((url: string) => void) | null = null;
let tapListenerAttached = false;

/**
 * A tap that arrived before the router did.
 *
 * One, not a queue: two notifications tapped before the app has painted is not
 * a thing that happens, and if it did, the second is the one the person meant.
 */
let pendingTapUrl: string | null = null;

/**
 * Whether a URL from a notification is safe to navigate to.
 *
 * In-app paths only. A notification is data arriving from outside, and an
 * absolute URL here would be an open redirect wearing a bell icon.
 */
function isInAppPath(url: unknown): url is string {
  return typeof url === 'string' && url.startsWith('/') && !url.startsWith('//');
}

/**
 * Starts listening for taps.
 *
 * Called at start-up, before React renders, so a tap that launched the app is
 * caught. Safe to call more than once — React mounts twice in development, and
 * two listeners means one tap navigating twice, which lands the man on the
 * right screen and then somewhere else.
 */
export async function attachPushTapListener(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  if (tapListenerAttached) return;
  tapListenerAttached = true;

  const { PushNotifications } = await import('@capacitor/push-notifications');
  await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    const url = action.notification.data?.url;
    if (!isInAppPath(url)) return;

    if (tapHandler) tapHandler(url);
    /* Held rather than dropped. The router is seconds away and this is the
       whole reason the person opened the app. */
    else pendingTapUrl = url;
  });
}

/**
 * Registers the router's navigate, and delivers anything that arrived first.
 *
 * Called from the shell on every render where navigate changes, so the handler
 * is always the current one.
 */
export async function handlePushTaps(navigate: (url: string) => void): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  tapHandler = navigate;
  await attachPushTapListener();

  if (pendingTapUrl !== null) {
    const url = pendingTapUrl;
    pendingTapUrl = null;
    navigate(url);
  }
}
